import cx from "classnames";
import type { AssistantMessageBlock } from "../../types/assistant";
import type { Photo, Video } from "../../types";
import { PhotoWidget } from "../PhotoWidget";
import { MarkdownBlock } from "./MarkdownBlock";
import { useMessageBlocksAnimation } from "../../hooks/useMessageBlocksAnimation";
import styles from "./AssistantMessage.module.css";
import { ReactNode, useMemo } from "react";
import { exhaustivenessCheck } from "../../utils/misc";

type AssistantMessageProps = {
  blocks: AssistantMessageBlock[];
  disableAnimation?: boolean;
  onComplete?: () => void;
  onUpdate?: () => void;
  onPhotosClick: (items: Array<Photo | Video>) => void;
};

const TYPEWRITER_CHAR_INTERVAL = 25;
const PHOTO_FADE_DURATION = 360;

export const AssistantMessage = ({
  blocks,
  disableAnimation,
  onComplete,
  onUpdate,
  onPhotosClick,
}: AssistantMessageProps) => {
  const { segmentedBlocks, blockIndex, stepIndex } = useMessageBlocksAnimation(
    blocks,
    {
      disableAnimation,
      charInterval: TYPEWRITER_CHAR_INTERVAL,
      photoDuration: PHOTO_FADE_DURATION,
      onComplete,
      onUpdate,
    },
  );

  const res: ReactNode[] = useMemo(() => {
    const nodes: ReactNode[] = [];
    if (segmentedBlocks.length === 0) {
      return nodes;
    }
    const lastIndex = Math.min(blockIndex, segmentedBlocks.length - 1);

    for (let i = 0; i <= lastIndex; i++) {
      const cur = segmentedBlocks[i];
      if (!cur) {
        break;
      }

      switch (cur.type) {
        case "photos":
          nodes.push(
            <PhotoWidget
              key={`photos-${i}`}
              items={cur.items}
              onClick={(items) => onPhotosClick(items)}
              className={cx(
                styles.photoBlock,
                !disableAnimation && styles.photoBlockAnimated,
              )}
              style={
                disableAnimation
                  ? undefined
                  : {
                      animationDuration: `${PHOTO_FADE_DURATION}ms`,
                    }
              }
            />,
          );
          break;

        case "paragraph":
          nodes.push(
            <p key={`paragraph-${i}`} className={styles.paragraph}>
              {i === blockIndex
                ? cur.text.slice(0, stepIndex)
                : cur.text}
            </p>,
          );
          break;

        case "markdown": {
          const charPos = i === blockIndex
            ? cur.charPositions[stepIndex - 1] ?? 0
            : cur.content.length;
          const visibleContent = cur.content.slice(0, charPos);
          nodes.push(
            <MarkdownBlock
              key={`markdown-${i}`}
              content={visibleContent}
              images={cur.images}
              onPhotosClick={onPhotosClick}
            />,
          );
          break;
        }

        default:
          exhaustivenessCheck(cur);
      }
    }

    return nodes;
  }, [
    blockIndex,
    disableAnimation,
    onPhotosClick,
    segmentedBlocks,
    stepIndex,
  ]);

  return (
    <div className={styles.blocks}>
      {res}
    </div>
  );
};
